import React, { useState , useEffect} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../helpers/AuthProvider";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Avatar,
	Box,
	Breadcrumbs,
	Button,
	Chip,
	Container,
	Divider,
	Grid,
	IconButton,
	Link,
	Paper,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import queryGraphql from "../../helpers/ApiInterface";
import moment from "moment";
import { ExpandMore } from "@mui/icons-material";
import { RiRestartLine } from "react-icons/ri";
import { RiCheckLine } from "react-icons/ri";
import { RiCloseLine } from "react-icons/ri";
import { RiHourglass2Line } from "react-icons/ri";
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
const statusStyles = {
	Loading: {
		backgroundImage: 'linear-gradient(to left, rgba(24, 24, 24, 0), rgba(24, 24, 24, 0.1))',
		icon:<RiRestartLine/>,
		status: ['active', 'pending','rejected']
	},
	Success: {
		backgroundImage: 'linear-gradient(to left, rgba(18, 183, 106, 0), rgba(18, 183, 106, 0.1))',
		icon:<RiCheckLine/>,
		status: ['Initiated','Fact File Created','Plan Successful','App Creation Initiated', 'App Creation Completed','Infra Audit Successful']
	},
	Error: {
		backgroundImage: 'linear-gradient(to left, rgba(240, 68, 56, 0), rgba(240, 68, 56, 0.1))',
		icon:<RiCloseLine/>,
		status: ['Plan Failed','App Creation Failed','Infra Audit Failed'],
	},
	Pending: {
		backgroundImage: 'linear-gradient(to left, rgba(253, 176, 34, 0), rgba(253, 176, 34, 0.1))',
		icon:<RiHourglass2Line/>,
		status: ['MR Approval Pending'],
	},

}
// --- Added Interface for Checkov Summary ---
interface CheckovSummary {
    passed: number;
    failed: number;
}
const ApplicationDetailsPageContent: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { id } = useParams();
	const [application, setApplication] = useState<any>({});
	const [resourceStatus, setResourceStatus] = useState<any>({});
	const [checkovSummary, setCheckovSummary] = useState<CheckovSummary | null>(null);
	const [isLoadingSecurityData, setIsLoadingSecurityData] = useState<boolean>(false);
	const GET_CHECKOV_RESULTS_QUERY = `
	query GetCheckovResults {
		getCheckovResults {
			id
			resourceType # Needed for potential filtering
			resourceId   # IMPORTANT: Assume this holds the cluster ID for cluster-related checks
			summary {
				passed
				failed
			}
			# application { id applicationName } # Include if needed for filtering instead of resourceId
		}
	}
`;
	React.useEffect(()=>{
		const fetchData = async () =>{
			try{
				const data = await queryGraphql(`query ResourceStatus($resourceStatusId: String!) {
  resourceStatus(id: $resourceStatusId) {
    id
    resourceId
    resourceName
    currentStatus
    resourceType
    resourceEnvironment
    resourceOwner {
      id
      name
    }
    projectUrl
    gcpClusterUrl
    argoCdStaticUrl
    subGroupProjectAppUrl
    subGroupProjectInfraUrl
    createdAt
    updatedAt
  }
}`, {resourceStatusId: id});
if(data.resourceStatus){
	// console.dir(data.cluster);
	setResourceStatus(data.resourceStatus);
	
}
			}
			catch(error){
				console.log(error);
			}
		}
		fetchData();
	},[]);


	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await queryGraphql(
					`
                        query($applicationId: String!){
                        application(id:$applicationId) {
						id,
                        applicationName,
                        applicationOwner{
                        id,
                        name,
                        googleProfilePicture
                        },
                        environment,
                        gcpApplicationViewers,
                        serviceRequesters,
                        costLabelPairs{
                        cost,
                        label
                        },
                        status,
                        createdAt,
						actionedAt,
						reasonForRejection,
                        cluster{
                        clusterName,
                        id,
                        },
						services{
						id,
						serviceName,
						serviceOwner{
						id,
						name,
						email,
						googleProfilePicture,

						},
						framework,
						gcpServiceViewers,
						serviceRequesters,
						status,
						createdAt
						}
                        }
                        }
`,
					{ applicationId: id }
				);
				if (data.application) {
					console.dir(data.application);
					setApplication(data.application);
				}
			} catch (error) {
				console.log(error);
			}
		};

		fetchData();
	}, []);
	useEffect(() => {
        const fetchAndFilterSecurityData = async () => {
            // Ensure cluster ID is available and cluster is active before fetching
            if (!id || !application.id || application.status !== 'active') {
                setCheckovSummary(null);
                
                setIsLoadingSecurityData(false); // Ensure loading is set to false if we don't fetch
                return;
            }

            setIsLoadingSecurityData(true);
            setCheckovSummary(null); // Reset before fetching/filtering
            

            console.log(`Workspaceing security data for cluster ID: ${id}`); // Debug log

            try {
                // Fetch ALL Checkov & Kyverno Results Concurrently
                console.log("Fetching all Checkov and Kyverno results...");
                const [checkovData] = await Promise.all([
                     queryGraphql(GET_CHECKOV_RESULTS_QUERY)
                 ]);

                // Filter Checkov Results
                 if (checkovData?.getCheckovResults && Array.isArray(checkovData.getCheckovResults)) {
                     console.log(`Received ${checkovData.getCheckovResults.length} Checkov results. Filtering...`);
                    // IMPORTANT: Verify 'resourceId' is the correct field linking to the cluster 'id'
                    const clusterCheckovResult = checkovData.getCheckovResults.find(
                        (result: any) => result.resourceId === id // Adjust filter key if needed (e.g., result.cluster?.id)
                    );

                    if (clusterCheckovResult && clusterCheckovResult.summary) {
                         console.log("Found matching Checkov result:", clusterCheckovResult.summary);
                        setCheckovSummary(clusterCheckovResult.summary);
                    } else {
                        console.log("No matching Checkov result found for this cluster.");
                        setCheckovSummary(null);
                    }
                } else {
                     console.log("No Checkov results received or data is not an array.");
                     setCheckovSummary(null);
                }

                // Filter Kyverno Results
                 

            } catch (error) {
                console.error("Error fetching/filtering security data:", error);
                setCheckovSummary(null); // Ensure state is null on error
                
            } finally {
                setIsLoadingSecurityData(false);
                 console.log("Finished fetching/filtering security data.");
            }
        };

        // Fetch only when cluster data is loaded and status is active
        fetchAndFilterSecurityData();

    }, [id, application.id, application.status]); // Depend on cluster data being loaded and id
	return (
		<Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
				<MiniBot/>
			<Box sx={{ p: 2, bgcolor: "#fff" }}>
				<Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
					<Link underline="hover" color="inherit" href="/applications">
						Applications
					</Link>
					<Typography color="text.primary">Application Details</Typography>
				</Breadcrumbs>
				<Box display="flex" alignItems="center" mt={1}>
					<IconButton
						edge="start"
						aria-label="back"
						color="inherit"
						onClick={() => navigate("/applications")}
					>
						<ArrowBackIosNewIcon fontSize="small" />
					</IconButton>
					<Typography fontFamily="Gilroy-Bold" fontSize="18px">
						Application Details
					</Typography>
				</Box>
			</Box>
			{application.id && (
				<Grid container>
					<Grid item xs={3}>
						<Box m={2} bgcolor="white" borderRadius="4px">
							<Box
								bgcolor={
									application.status == "active"
										? "#12B76A10"
										: application.status == "rejected"
										? "#F0443810"
										: "#FDB02210"
								}
								p={3}
							>
								<Box display="flex" justifyContent="space-between" alignItems="flex-start">
									<Typography fontFamily="Gilroy-Bold" fontSize="20px">
										{application.applicationName}
									</Typography>
									{application.status == "pending" &&
										user.roles?.some((e: string) => ["application_owner"].includes(e)) && (
											<IconButton
												edge="start"
												aria-label="back"
												color="inherit"
												onClick={() => navigate(`/applications/edit/${application.id}`)}
											>
												<img width="20px" height="20px" src="/assets/edit-icon.svg" />
											</IconButton>
										)}
								</Box>
								<Box display="flex" justifyContent="space-between" alignItems="center">
									<Typography fontFamily="Gilroy-Regular" fontSize="12px">
										{moment(new Date(Number(application.createdAt)).toISOString()).format(
											"DD/MM/YYYY | hh:mm A"
										)}
									</Typography>
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<img
											src={
												application.status == "active"
													? "/assets/tickcircle.svg"
													: application.status == "rejected"
													? "/assets/reject.svg"
													: "/assets/clock.svg"
											}
											alt="Icon"
											style={{ width: "14px", marginRight: "2px" }}
										/>
										<Typography
											fontSize="12px"
											fontFamily="Gilroy-Regular"
											color={
												application.status == "active"
													? "#12B76A"
													: application.status == "rejected"
													? "#F04438"
													: "#FDB022"
											}
										>
											{application.status == "active"
												? "Active"
												: application.status == "rejected"
												? "Rejected"
												: "Pending Approval"}
										</Typography>
									</Box>
								</Box>
							</Box>
							<Box sx={{maxHeight:'80vh',overflowY: "auto"}}>
							<Grid container gap={1} p={3}>
								<Grid mb={2} item xs={5.75}>
									<Typography fontFamily="Gilroy-Regular" fontSize="12px">
										Cluster Name
									</Typography>
									<Typography fontFamily="Gilroy-SemiBold" fontSize="14px">
										{application.cluster?.clusterName ? application.cluster?.clusterName : "-"}
									</Typography>
								</Grid>
								<Grid mb={2} item xs={5.75}>
									<Typography fontFamily="Gilroy-Regular" fontSize="12px">
										Application Owner
									</Typography>
									<Box display="flex" alignItems="center" justifyContent="flex-start">
										<Avatar
											sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
											src={application.applicationOwner?.googleProfilePicture}
										/>
										<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
											{application.applicationOwner?.name}
										</Typography>
									</Box>
								</Grid>
								<Grid mb={2} item xs={5.75}>
									<Typography fontFamily="Gilroy-Regular" fontSize="12px">
										Environment
									</Typography>
									<Typography fontFamily="Gilroy-SemiBold" fontSize="14px">
										{application.environment}
									</Typography>
								</Grid>
								<Grid mb={2} item xs={5.75}>
									<Typography fontFamily="Gilroy-Regular" fontSize="12px">
										Application Viewers
									</Typography>
									<Box display="flex" alignItems="center" justifyContent="flex-start">
										{application.gcpApplicationViewers?.map((e: string) => {
											return (
												<Tooltip title={e}>
													<Avatar
														sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
														src={e}
														alt={e}
													/>
												</Tooltip>
											);
										})}
									</Box>
								</Grid>
								<Grid mb={2} item xs={5.75}>
									<Typography fontFamily="Gilroy-Regular" fontSize="12px">
										Service Owners
									</Typography>
									<Box display="flex" alignItems="center" justifyContent="flex-start">
										{application.serviceRequesters?.map((e: string) => {
											return (
												<Tooltip title={e}>
													<Avatar
														sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
														src={e}
														alt={e}
													/>
												</Tooltip>
											);
										})}
									</Box>
								</Grid>
									<Grid mb={2} item xs={5.75} >
									<Typography fontFamily="Gilroy-Regular" fontSize="12px" >
										Infra URL
									</Typography>
									
									<Typography fontFamily="Gilroy-SemiBold" fontSize={14} sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' , display:"flex", alignItems: "center"}}>
									2
									<Tooltip 
										sx={{ whiteSpace: "pre-line" }}
										// title={cluster.applications?.map(
										// 	(e: any, index: number) => `${e.applicationName}`
										// )}
										title={
											<>
											<Typography fontFamily="Gilroy-Regular" fontSize="14px" fontWeight={700}>

												Infra URL
											</Typography>
												<br />
												{resourceStatus.resourceId === id && resourceStatus.projectUrl ? (
													<Link
														href={resourceStatus.projectUrl}
														target="_blank"
														rel="noopener noreferrer"
														sx={{
															textDecoration: 'underline', // Ensure underline for link appearance
															color: 'inherit' // Inherit color from Typography, or set explicitly if needed
														}}
													>
														<Typography
															fontFamily="Gilroy-Bold"
															fontSize="12px"
															// Apply link color here if 'inherit' doesn't work as expected,
															// or if you want a specific link color different from default Typography.
															// For example: color: '#ADD8E6' (light blue)
														>
															App URL
														</Typography>
													</Link>
												) : (
													// If no link available (loading state), display the label as plain text
													<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																	App URL
												
													</Typography>
												)}
												{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
										App URL
									</Typography>
									<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
										{resourceStatus.resourceId === id ? (
											<Link href={resourceStatus.projectUrl} 
											target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: '#FFFFFF' }}> 
											{`${resourceStatus.projectUrl.substring(0,10)}...`} 
											</Link>
													) : (
												<>Loading...</>
														)}
											</Typography> */}
												<br/>
												{resourceStatus.resourceId === id && resourceStatus.subGroupProjectInfraUrl ? (
													<Link
														href={resourceStatus.subGroupProjectInfraUrl}
														target="_blank"
														rel="noopener noreferrer"
														sx={{
															textDecoration: 'underline', // Ensure underline for link appearance
															color: 'inherit' // Inherit color from Typography, or set explicitly if needed
														}}
													>
														<Typography
															fontFamily="Gilroy-Bold"
															fontSize="12px"
															// Apply link color here if 'inherit' doesn't work as expected,
															// or if you want a specific link color different from default Typography.
															// For example: color: '#ADD8E6' (light blue)
														>
															K8s Infra URL
														</Typography>
													</Link>
												) : (
													// If no link available (loading state), display the label as plain text
													<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																	K8s Infra URL
												
													</Typography>
												)}
												{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
										K8s Infra URL
									</Typography>
									<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
										{resourceStatus.resourceId === id ? (
											<Link href={resourceStatus.subGroupProjectInfraUrl} 
											target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: '#FFFFFF' }}> 
											{`${resourceStatus.subGroupProjectInfraUrl.substring(0,10)}...`} 
											</Link>
													) : (
												<>Loading...</>
														)}
											</Typography> */}

												<br/>

											

											</>
										}
										slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
									>
										<Avatar
											src="/assets/info.svg"
											alt="info"
											sx={{ height: 16, width: 16, marginLeft: "4px" }}
										/>
										
									</Tooltip>
											</Typography>
								</Grid>	
								<Grid item xs={12} sm={6}>
																				<Typography fontFamily="Gilroy-Regular" fontSize="12px" > Installed Plugins </Typography>
																				<Box display="flex" alignItems="center">
																					<Typography fontFamily="Gilroy-SemiBold" fontSize={14} sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' , display:"flex", alignItems: "center"}}>
																					{ [ checkovSummary].filter(Boolean).length}
																			<Tooltip 
																				sx={{ whiteSpace: "pre-line" }}
																				// title={cluster.applications?.map(
																				// 	(e: any, index: number) => `${e.applicationName}`
																				// )}
																				title={
																					<>
																					<Typography fontFamily="Gilroy-Regular" fontSize="14px" fontWeight={700}>
								
																						Installed Plugins
																					</Typography>
																						<br />
																						{checkovSummary && (
																							<Typography
																							fontFamily="Gilroy-Regular"
																							fontSize="12px"
																							// Apply link color here if 'inherit' doesn't work as expected,
																							// or if you want a specific link color different from default Typography.
																							// For example: color: '#ADD8E6' (light blue)
																						>
																							Checkov
																						</Typography>
																						)}
																						{checkovSummary && (
																							
									<Link
										href="/integrations/checkov-details"
										
										sx={{
											textDecoration: 'underline', // Ensure underline for link appearance
											color: 'inherit' // Inherit color from Typography, or set explicitly if needed
										}}
									>
										<Typography
											fontFamily="Gilroy-Bold"
											fontSize="12px"
											// Apply link color here if 'inherit' doesn't work as expected,
											// or if you want a specific link color different from default Typography.
											// For example: color: '#ADD8E6' (light blue)
										>
											Passed Checks:{checkovSummary.passed} | Failed Checks:{checkovSummary.failed}
										</Typography>
									</Link>
								)}
								
								
																						{/* <Typography fontFamily="Gilroy-Regular" fontSize="12px">
																				Cluster URL
																			</Typography>
																			<Typography fontFamily="Gilroy-SemiBold" fontSize="14px" sx={{ wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', color: '#FFFFFF' }}>
																				{resourceStatus.resourceId === id ? (
																					<Link href={resourceStatus.gcpClusterUrl} 
																					target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', color: '#FFFFFF' }}> 
																					{`${resourceStatus.gcpClusterUrl.substring(0,10)}...`} 
																					</Link>
																							) : (
																						<>Loading...</>
																								)}
																					</Typography> */}
								
																						<br/>
																						
																						
								
								
																					</>
																				}
																				slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
																			>
																				
																				<Avatar
																					src="/assets/info.svg"
																					alt="info"
																					sx={{ height: 16, width: 16, marginLeft: "4px" }}
																				/>
																				
																			</Tooltip>
																					</Typography>
																					
																				</Box>
																			</Grid>									
								<Grid mb={2} item xs={11}>
									<Typography fontFamily="Gilroy-Regular" fontSize="12px">
										Tags/Label
									</Typography>
									<Grid container ml={-0.5} gap={0.75}>
										{application.costLabelPairs?.map((e: any) => {
											return (
												<Grid item>
													<Chip
														size="small"
														sx={{
															bgcolor: "#10101010",
															border: "1px solid black",
															mr: 0.75,
															fontFamily: "Gilroy-Regular",
															fontSize: "12px",
														}}
														variant="filled"
														label={`${e.cost} • ${e.label}`}
													/>
												</Grid>
											);
										})}
									</Grid>
								</Grid>
							</Grid>
							</Box>

							<Grid  item xs={11.75} sx={{display:"flex", alignItems:"space-between", backgroundImage: (statusStyles.Loading.status.includes(resourceStatus.currentStatus)) ? statusStyles.Loading.backgroundImage :  statusStyles.Success.status.includes(resourceStatus.currentStatus) ? statusStyles.Success.backgroundImage :  statusStyles.Error.status.includes(resourceStatus.currentStatus) ? statusStyles.Error.backgroundImage : statusStyles.Pending.backgroundImage 
																	
																}} >
							
																	<Box   sx={{mx:3, my:1 }}> <Typography fontSize="18px" width= "300px"  sx={{display:"flex", alignItems:"center"}}> <>{ (statusStyles.Loading.status.includes(resourceStatus.currentStatus)) ? statusStyles.Loading.icon : statusStyles.Success.status.includes(resourceStatus.currentStatus) ? statusStyles.Success.icon : statusStyles.Error.status.includes(resourceStatus.currentStatus) ? statusStyles.Error.icon : statusStyles.Pending.icon  } </> <Typography sx={{ml:1}}>{(statusStyles.Loading.status.includes(resourceStatus.currentStatus))? null :resourceStatus.currentStatus}</Typography>  </Typography>
																	</Box>
							
							
																</Grid>	

						</Box>
					</Grid>
					<Grid item xs={9}>
						<Box
							bgcolor="white"
							minHeight="100%"
							display="flex"
							flexDirection="column"
							m={2}
							borderRadius="8px"
						>
							<Box display="flex" alignItems="center" justifyContent="space-between">
								<Typography fontFamily="Gilroy-SemiBold" fontSize="16px" px={2} py={3}>
									{`SERVICES (${application.services?.length || 0})`}
								</Typography>
								{application.services && application.services?.length > 0 && (
									<Button
										sx={{
											backgroundColor: "black",
											color: "white",
											fontWeight: "bold",
											"&:hover": {
												backgroundColor: "white",
												color: "black",
												borderColor: "black",
											},
											textTransform: "none",
											px: 2,
											py: 0.5,
											mr: 2,
										}}
										disabled={!user.roles?.some((e: string) => ["developer"].includes(e))}
										variant="contained"
										size="large"
										onClick={() => navigate("/services/add")}
									>
										Add New Service
									</Button>
								)}
							</Box>
							<Divider />
							<Box sx={{maxHeight:'80vh',overflowY: "auto"}}>
							{application.status == "active" &&
							(!application.services || application.services?.length == 0) ? (
								<Box
									sx={{
										position: "relative",
										top: "50%",
										left: "50%",
										transform: "translate(-50%, -10%)",
										width: "700px",
									}}
									display="flex"
									flexDirection="column"
									flex={1}
									alignItems="center"
									justifyContent="center"
								>
									<img src="/assets/inbox-icon.svg" alt="empty" />
									<Typography
										gutterBottom
										fontFamily="Gilroy-Bold"
										fontSize="16px"
										px={2}
										textAlign="center"
									>
										No services are added yet
									</Typography>
									<Typography
										fontFamily="Gilroy-Regular"
										fontSize="14px"
										px={2}
										mb={2}
										textAlign="center"
									>
										Click below button to start adding services or ask the developer to add.
									</Typography>
									<Button
										sx={{
											backgroundColor: "black",
											color: "white",
											fontWeight: "bold",
											"&:hover": {
												backgroundColor: "white",
												color: "black",
												borderColor: "black",
											},
											textTransform: "none",
											px: 4,
										}}
										variant="contained"
										size="large"
										disabled={!user.roles?.some((e: string) => ["developer"].includes(e))}
										onClick={() => navigate("/services/add")}
									>
										Add New Service
									</Button>
								</Box>
							) : application.status == "active" &&
							  application.services &&
							  application.services?.length > 0 ? (
								<Box
									display="flex"
									flexDirection="column"
									flex={1}
									sx={{ p: 2, bgcolor: "#FFFFFF" }}
								>
									{application.services.map((service: any, index: number) => {
										return (
											<Accordion
												elevation={0}
												sx={{
													borderRadius: "8px",
													mb: "16px",
													"&:before": {
														display: "none",
													},
													bgcolor: "white",
													outline: "1px solid #eee",
												}}
											>
												<AccordionSummary expandIcon={null}>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "flex-start",
														}}
													>
														<Box p={1} bgcolor="#F2F2F2" borderRadius={1} mr={2}>
															<ExpandMore />
														</Box>
														<Box>
															<Typography fontSize="14px" fontFamily="Gilroy-SemiBold">
																{service.serviceName}
															</Typography>
															<Typography fontSize="12px" fontFamily="Gilroy-Regular">
																{moment(
																	new Date(Number(service.createdAt)).toISOString()
																).format("DD/MM/YYYY | hh:mm A")}
															</Typography>
														</Box>
													</Box>
												</AccordionSummary>
												<AccordionDetails>
													<Divider />
													<Grid container mt={2}>
														<Grid mb={2} item xs={2.4}>
															<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																Application Name
															</Typography>
															<Typography fontFamily="Gilroy-SemiBold" fontSize="14px">
																{application?.applicationName}
															</Typography>
														</Grid>
														<Grid mb={2} item xs={2.4}>
															<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																Cluster Name
															</Typography>
															<Typography fontFamily="Gilroy-SemiBold" fontSize="14px">
																{application?.cluster?.clusterName}
															</Typography>
														</Grid>
														<Grid mb={2} item xs={2.4}>
															<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																Framework
															</Typography>
															<Typography fontFamily="Gilroy-SemiBold" fontSize="14px">
																{service.framework}
															</Typography>
														</Grid>
														<Grid mb={2} item xs={2.4}>
															<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																Service Owner
															</Typography>
															<Box display="flex" alignItems="center" justifyContent="flex-start">
																<Avatar
																	sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
																	src={service.serviceOwner?.googleProfilePicture}
																/>
																<Typography fontFamily="Gilroy-Semibold" fontSize="14px">
																	{service.serviceOwner?.name}
																</Typography>
															</Box>
														</Grid>
														<Grid mb={2} item xs={2.4}>
															<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																Service Viewers
															</Typography>
															<Box display="flex" alignItems="center" justifyContent="flex-start">
																{service.gcpServiceViewers?.map((e: string) => {
																	return (
																		<Tooltip title={e}>
																			<Avatar
																				sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
																				src={e}
																				alt={e}
																			/>
																		</Tooltip>
																	);
																})}
															</Box>
														</Grid>
														<Grid mb={2} item xs={2.4}>
															<Typography fontFamily="Gilroy-Regular" fontSize="12px">
																Service Owners
															</Typography>
															<Box display="flex" alignItems="center" justifyContent="flex-start">
																{service.serviceRequesters?.map((e: string) => {
																	return (
																		<Tooltip title={e}>
																			<Avatar
																				sx={{ height: "20px", width: "20px", marginRight: 0.5 }}
																				src={e}
																				alt={e}
																			/>
																		</Tooltip>
																	);
																})}
															</Box>
														</Grid>
													</Grid>
												</AccordionDetails>
											</Accordion>
										);
									})}
								</Box>
							) : (
								<Box
									sx={{
										position: "relative",
										top: "50%",
										left: "50%",
										transform: "translate(-50%, -10%)",
										width: "700px",
									}}
									display="flex"
									flexDirection="column"
									flex={1}
									alignItems="center"
									justifyContent="center"
								>
									<img src="/assets/inbox-reject-icon.svg" alt="empty" />
									<Typography
										gutterBottom
										fontFamily="Gilroy-Bold"
										fontSize="16px"
										px={2}
										textAlign="center"
									>
										Service addition is not allowed
									</Typography>
									<Paper
										elevation={0}
										sx={{
											bgcolor: "#FDB02215",
											border: "1px solid #FDB022",
											display: "flex",
											alignItems: "center",
											p: 1,
										}}
									>
										<img src="/assets/info-circle.svg" />
										<Typography fontFamily="Gilroy-Regular" fontSize="14px" ml={1}>
											{application.status == "rejected"
												? "Application is rejected by the approver, and addition of service is no longer applicable."
												: "Application is still pending approval, once the application is approved you can start adding services."}
										</Typography>
									</Paper>
								</Box>
							)}
							</Box>
						</Box>
					</Grid>
				</Grid>
			)}
		</Container>
	);
};

// export default ApplicationDetailsPage;
const ApplicationDetailsPage: React.FC = () => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    // Using your remembered calculation for marginLeft
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                // Assuming "integrations" is the correct index for Kyverno page
                // If Kyverno has its own top-level menu item, update this index.
                index={"applications"} // Or a more specific index like "integrations-kyverno" if applicable
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // bgcolor: '#fafafa', // bgcolor commented out as per your previous change
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    // Using your remembered unit and calculation for marginLeft
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <ApplicationDetailsPageContent />
            </Box>
        </Box>
    );
};

export default ApplicationDetailsPage;